import { useState, useCallback } from 'react';

// Global rate limiter to prevent excessive API calls
class ApiRateLimiter {
  private lastCallTime = new Map<string, number>();
  private pendingCalls = new Map<string, Promise<any>>();
  private readonly minInterval = 1000; // 1 second between calls

  shouldAllowCall(key: string): boolean {
    const now = Date.now();
    const lastCall = this.lastCallTime.get(key) || 0;
    
    if (now - lastCall < this.minInterval) {
      console.log(`Rate limiting API call: ${key}`);
      return false;
    }
    
    this.lastCallTime.set(key, now);
    return true;
  }

  // Debounce function to prevent rapid successive calls
  debounce(key: string, func: () => Promise<any>, delay = 500): Promise<any> {
    const existingCall = this.pendingCalls.get(key);
    if (existingCall) {
      return existingCall;
    }

    const debouncedCall = new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          if (this.shouldAllowCall(key)) {
            const result = await func();
            resolve(result);
          } else {
            resolve(null); // Return null if rate limited
          }
        } catch (error) {
          reject(error);
        } finally {
          this.pendingCalls.delete(key);
        }
      }, delay);
    });

    this.pendingCalls.set(key, debouncedCall);
    return debouncedCall;
  }

  reset(): void {
    this.lastCallTime.clear();
    this.pendingCalls.clear();
  }
}

export const apiRateLimiter = new ApiRateLimiter();

// Hook for rate-limited API calls
export const useRateLimitedApi = () => {
  const [isLoading, setIsLoading] = useState(false);

  const callApi = useCallback(async (key: string, apiFunc: () => Promise<any>, delay = 500) => {
    setIsLoading(true);
    try {
      const result = await apiRateLimiter.debounce(key, apiFunc, delay);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { callApi, isLoading };
};
